// src/components/aiinsight/ResultPanel.jsx
// Right-side live result panel — shown after every AI query.
// Splits a single query result into 3 stacked sections:
//   DATA     → chart / table
//   INSIGHTS → AI narrative text
//   OUTCOMES → follow-up actions + pin controls

import React, { useMemo, useState } from 'react';
import {
    Box, IconButton, Typography, Tooltip, Button,
    Chip, Collapse, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import InsightsIcon from '@mui/icons-material/Insights';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';
import AiChartRenderer from './AiChartRenderer';
import VisualErrorBoundary from './VisualErrorBoundary';
import InsightSummary from './InsightSummary';

const PANEL_WIDTH = 420;

// Strip [Format instructions:...] appended by the chat bar before using as a display title
const stripQuery = (prompt = '') =>
    prompt.replace(/\s*\[Format instructions?:[^\]]*\]/gi, '').trim();

// ── Narrative tag parser ──────────────────────────────────────────────────────
const parseNarrative = (text) => {
    if (!text) return null;
    const parts = text.split(/(<good>.*?<\/good>|<bad>.*?<\/bad>|<b>.*?<\/b>)/gis);
    return parts.map((part, i) => {
        const lowerPart = part.toLowerCase();
        if (lowerPart.startsWith('<good>'))
            return <span key={i} style={{ color: '#22c55e', fontWeight: 600 }}>{part.replace(/<\/?good>/gi, '')}</span>;
        if (lowerPart.startsWith('<bad>'))
            return <span key={i} style={{ color: '#f43f5e', fontWeight: 600 }}>{part.replace(/<\/?bad>/gi, '')}</span>;
        if (lowerPart.startsWith('<b>'))
            return <strong key={i}>{part.replace(/<\/?b>/gi, '')}</strong>;
        return <span key={i}>{part}</span>;
    });
};

// ── Compact number formatter ────────────────────────────────────────────────
const compactNum = (raw, forceDollar = false) => {
    if (raw === null || raw === undefined || raw === '') return '—';
    try {
        const valStr = String(raw);
        const hasDollar = forceDollar || valStr.startsWith('$');
        const hasPercent = valStr.endsWith('%');
        // Strip $ and commas before parsing so parseFloat works correctly
        const str = valStr.replace(/[$,]/g, '');
        const n = parseFloat(str);
        if (isNaN(n)) return valStr;
        let formatted;
        if (Math.abs(n) >= 1e9) formatted = `${(n / 1e9).toFixed(1)}B`;
        else if (Math.abs(n) >= 1e6) formatted = `${(n / 1e6).toFixed(1)}M`;
        else if (Math.abs(n) >= 1e3) formatted = `${(n / 1e3).toFixed(1)}K`;
        else formatted = n % 1 === 0 ? String(n) : n.toFixed(2);
        return (hasDollar ? '$' : '') + formatted + (hasPercent ? '%' : '');
    } catch (e) {
        return String(raw);
    }
};

// ── Operational Story Bullets ─────────────────────────────────────────────────
const NarrativeBullets = ({ text, sx = {} }) => {
    if (!text) return null;

    const detectSentiment = (rawBullet) => {
        if (/<good>/i.test(rawBullet)) return 'good';
        if (/<bad>/i.test(rawBullet)) return 'bad';
        const lower = rawBullet.toLowerCase();
        if (/\b(grow|increas|improv|exceed|above|positive|rise|gain|higher|opportunit)\b/.test(lower)) return 'good';
        if (/\b(declin|drop|fall|concern|below|negative|loss|reduc|cancel|risk|warn|alert)\b/.test(lower)) return 'bad';
        if (/\b(recommend|action|suggest|ensure|consider|should|opportunity)\b/.test(lower)) return 'insight';
        if (/\b(trend|track|monitor|analyz|pattern|correlat)\b/.test(lower)) return 'trend';
        return 'neutral';
    };

    const sentimentStyle = {
        good: { icon: '✓', color: '#10b981', bg: 'rgba(16,185,129,0.06)', border: '#10b981' },
        bad: { icon: '!', color: '#f43f5e', bg: 'rgba(244,63,94,0.06)', border: '#f43f5e' },
        insight: { icon: '💡', color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', border: '#f59e0b' },
        trend: { icon: '↗', color: '#6366f1', bg: 'rgba(99, 102, 241, 0.06)', border: '#6366f1' },
        neutral: { icon: '●', color: '#6366f1', bg: 'rgba(99, 102, 241, 0.04)', border: 'rgba(99, 102, 241, 0.25)' },
    };

    const rawLines = text.split(/\n+/);
    const bullets = [];
    rawLines.forEach(line => {
        let trimmed = line.trim();
        if (!trimmed) return;

        // Determine whether this line was explicitly formatted as a bullet point
        const isBullet = /^[-*•]\s+|^\d+\.\s+/.test(trimmed);

        // Strip out leading bullet points, hyphens, or numbers
        trimmed = trimmed.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, '');

        if (trimmed.length > 3) {
            bullets.push({ raw: line, formatted: trimmed, isBullet });
        }
    });

    if (bullets.length === 0) return null;

    const parseWithNumbers = (str) => {
        // Strip generic XML tags, keep markdown bold
        const safeStr = str.replace(/<\/?(?:good|bad|b)>/gi, '');
        // Split by markdown bold **...**
        const mdParts = safeStr.split(/(\*\*.*?\*\*)/gis);
        const out = [];

        mdParts.forEach((part, pi) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                out.push(<strong key={pi}>{part.slice(2, -2)}</strong>);
            } else {
                part.split(/(\$?[\d,]+\.?\d*%?|\d+\.\d+%?)/g).forEach((seg, si) => {
                    const clean = seg.replace(/,/g, '');
                    const isYear = /^\d{4}$/.test(clean) && +clean >= 1900 && +clean <= 2099;
                    const isIdLike = /^\d{5,}$/.test(clean); // bare 5+ digit integer = likely an ID/key
                    if (!isYear && !isIdLike && /^(\$?[\d,]+\.?\d*%?|\d+\.\d+%?)$/.test(seg) && seg.length > 0)
                        out.push(<span key={`${pi}_${si}`} style={{ color: '#f59e0b', fontWeight: 700 }}>{compactNum(seg)}</span>);
                    else out.push(<span key={`${pi}_${si}`}>{seg}</span>);
                });
            }
        });
        return out;
    };

    return (
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
            {bullets.map(({ formatted, raw, isBullet }, i) => {
                if (!isBullet) {
                    return (
                        <Box key={i} component="li" sx={{ px: 1, py: 0.25 }}>
                            <Box component="span" sx={{ fontSize: 13, lineHeight: 1.65, color: 'text.primary', display: 'block' }}>
                                {parseWithNumbers(formatted)}
                            </Box>
                        </Box>
                    );
                }

                const sentiment = detectSentiment(raw);
                const { icon, color, bg, border } = sentimentStyle[sentiment];
                return (
                    <Box key={i} component="li" sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.25,
                        px: 1.5, py: 0.9, borderRadius: 1.5, bgcolor: bg,
                        borderLeft: `3px solid ${border}`,
                    }}>
                        <Box component="span" sx={{
                            flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                            border: `1.5px solid ${color}`, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: sentiment === 'insight' ? 11 : 10,
                            fontWeight: 700, color, mt: '1px', bgcolor: `${color}18`,
                        }}>{icon}</Box>
                        <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.65, color: 'text.primary', flex: 1 }}>
                            {parseWithNumbers(formatted)}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};


// ── Section shell ─────────────────────────────────────────────────────────────
const SectionShell = ({ icon, label, subtitle, color, bg, border, onPin, pinned, onEdit, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box sx={{ borderRadius: 2.5, overflow: 'hidden', flexShrink: 0 }}>
            {/* Header */}
            <Box
                onClick={() => setOpen(o => !o)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 1,
                    background: bg,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { filter: 'brightness(1.1)' },
                }}
            >
                <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color, letterSpacing: 0.5, display: 'block', lineHeight: 1.2 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" sx={{ color, opacity: 0.7, fontSize: 10 }}>
                        {subtitle}
                    </Typography>
                </Box>
                {onEdit && (
                    <Tooltip title="Edit narrative">
                        <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); onEdit(); }}
                            sx={{ color: 'text.disabled', '&:hover': { color }, width: 24, height: 24 }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={pinned ? 'Pinned to Report' : 'Pin to Report Builder'}>
                    <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); onPin?.(); }}
                        sx={{
                            color: pinned ? color : 'text.disabled',
                            '&:hover': { color },
                            width: 24, height: 24,
                        }}
                    >
                        {pinned ? <PushPinIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                    </IconButton>
                </Tooltip>
                {open
                    ? <KeyboardArrowUpIcon sx={{ fontSize: 14, color, opacity: 0.7 }} />
                    : <KeyboardArrowDownIcon sx={{ fontSize: 14, color, opacity: 0.7 }} />
                }
            </Box>

            {/* Body */}
            <Collapse in={open}>
                <Box sx={{ p: 1.5 }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

// ── Format numeric values based on column context ─────────────────────────────
const formatNumericValue = (value, fieldName = '') => {
    try {
        if (value === null || value === undefined || value === '') return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        const fn = String(fieldName || '').toLowerCase();
        if (fn === 'year' || fn.endsWith('_year') || fn.endsWith(' year')) return String(value);
        const isCurrency = fn.match(/(amount|payment|revenue|cost|price|fee|balance|total_pay|reimbursement|billing|spend|budget)/);
        if (isCurrency) {
            return compactNum(num, true);
        }
        return num % 1 === 0 ? num.toLocaleString() : num.toFixed(2);
    } catch (e) {
        return String(value);
    }
};

// ── Mini data table ───────────────────────────────────────────────────────────
const MiniTable = ({ data }) => {
    if (!data || !data.length || !data[0]) return null;
    const columns = Object.keys(data[0]).map(key => ({
        field: key,
        headerName: String(key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        flex: 1, minWidth: 80,
        renderCell: (params) => (
            <span style={{ fontSize: 11 }}>
                {formatNumericValue(params?.value, key)}
            </span>
        )
    }));
    const rows = data.map((row, i) => ({ id: i, ...row }));
    return (
        <Box sx={{ height: 250, mt: 1 }}>
            <DataGrid
                rows={rows} columns={columns}
                density="compact" disableRowSelectionOnClick
                pageSizeOptions={[5]} initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                sx={{
                    border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 2, fontSize: 11,
                    '& .MuiDataGrid-columnHeaders': { background: 'rgba(99, 102, 241, 0.08)' },
                    '& .MuiDataGrid-cell': { borderColor: 'rgba(99, 102, 241, 0.08)' },
                }}
            />
        </Box>
    );
};

// ── Key metrics extractor ──────────────────────────────────────────────────────
// Finds numeric columns and computes max, min, avg per column (up to 2 cols)
const computeMetrics = (data) => {
    if (!data || data.length === 0) return [];
    // Check up to 10 rows to determine if a column is numeric (handles leading nulls)
    const isNumCol = (k) => {
        for (let i = 0; i < Math.min(data.length, 10); i++) {
            const v = data[i][k];
            if (typeof v === 'number' && !isNaN(v) && isFinite(v)) return true;
            if (v !== null && v !== undefined) return false;
        }
        return false;
    };

    // Only pick truly numeric columns for metrics (ignores ID/keys and strings)
    const cols = Object.keys(data[0]).filter(k => {
        const kl = k.toLowerCase();
        return isNumCol(k) && !kl.includes('id') && !kl.includes('key') && !kl.includes('year') && !kl.includes('month');
    });

    const metrics = [];
    cols.slice(0, 2).forEach(col => {
        const vals = data.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
        if (vals.length === 0) return;

        const max = Math.max(...vals);
        const min = Math.min(...vals);
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;

        const isCurrency = col.toLowerCase().match(/(amount|payment|revenue|cost|price|fee|balance|total_pay|reimbursement|billing|spend|budget)/);
        const fmt = v => compactNum(v, isCurrency);

        metrics.push(
            { label: `Max ${col}`, value: fmt(max), color: '#22c55e' },
            { label: `Min ${col}`, value: fmt(min), color: '#f43f5e' },
            { label: `Avg ${col}`, value: fmt(avg), color: '#6366f1' },
        );
    });
    return metrics.slice(0, 4);
};

// ── ResultPanel ───────────────────────────────────────────────────────────────
const ResultPanel = ({ result, onClose, onPin, pinnedSections = {}, onRerun, readOnly = false }) => {
    if (!result) return null;

    const hasChart = result.viz_config_json &&
        result.viz_config_json.type !== 'table' &&
        (result.intent === 'visualize' || result.intent === 'compare');
    // Extract the actual data array. Sometimes backend sends { data: [], ... }
    const resultData = useMemo(() => {
        if (!result.result_json) return [];
        return Array.isArray(result.result_json) ? result.result_json : (result.result_json.data || []);
    }, [result.result_json]);

    const hasData = resultData.length > 0;
    const hasNarrative = !!result.summary_text;
    // intent='answer' means a semantic EXPLAIN query — no data rows expected, just a text answer
    const isAnswer = result.intent === 'answer' || result.intent === 'answer_off_domain';
    // intent='answer_off_domain' means the question was unrelated to this analytics domain
    const isOffDomain = result.intent === 'answer_off_domain';

    // DATA section: chart vs table toggle
    const [showTable, setShowTable] = useState(false);

    // Key metrics from data
    const metrics = useMemo(() => computeMetrics(resultData), [resultData]);

    // Inline edit state for INSIGHTS
    const [editingInsight, setEditingInsight] = useState(false);
    const [insightText, setInsightText] = useState(result.summary_text || '');
    const [savedInsight, setSavedInsight] = useState(result.summary_text || '');

    // Synchronize local state when the active result changes
    React.useEffect(() => {
        setSavedInsight(result.summary_text || '');
        setInsightText(result.summary_text || '');
        setEditingInsight(false);
        setShowTable(false);
    }, [result]);

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                // When inline in chat (readOnly), don't constrain height — let it expand naturally
                ...(readOnly ? {} : { height: '100%' }),
                bgcolor: 'background.default',
                borderRadius: '20px',
                // When inline, overflow must be visible so chat scroll area handles scrolling
                overflow: readOnly ? 'visible' : 'hidden',
                flexShrink: 0,
            }}
        >
            {/* Panel header */}
            {/* <Box sx={{
                px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                minHeight: 46, flexShrink: 0,
                background: 'linear-gradient(90deg, rgba(var(--primary-rgb), 0.08) 0%, transparent 100%)',
            }}>
                {onRerun && (
                    <Tooltip title="Re-run this query">
                        <IconButton size="small" onClick={() => onRerun(result.user_prompt?.replace(/\[Format instructions:.*?\]/g, '')?.trim())}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, width: 24, height: 24 }}>
                            <RefreshIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box> */}

            {/* Scrollable sections */}
            <Box sx={{
                // In readOnly (inline chat) mode — let content flow naturally, no y-scroll
                ...(readOnly
                    ? { p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }
                    : {
                        flex: 1, overflowY: 'auto', p: 1.5,
                        display: 'flex', flexDirection: 'column', gap: 1.5,
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-thumb': { background: 'rgb(var(--primary-rgb) / 0.2)', borderRadius: 2 },
                    }),
            }}>

                {/* ── OFF-DOMAIN WARNING card ── */}
                {isOffDomain && hasNarrative && (
                    <Box sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid rgba(245,158,11,0.35)',
                    }}>
                        {/* Warning header banner */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 2, py: 1.25,
                            background: 'linear-gradient(90deg, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0.08) 100%)',
                            borderBottom: '1px solid rgba(245,158,11,0.25)',
                        }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#f59e0b', letterSpacing: 0.5, fontSize: 11, display: 'block', lineHeight: 1.2 }}>
                                    OUT-OF-SCOPE QUESTION
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(245,158,11,0.7)', fontSize: 10 }}>
                                    This question is not related to your analytics domain
                                </Typography>
                            </Box>
                        </Box>
                        {/* Answer body */}
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: 'rgba(245,158,11,0.03)' }}>
                            <InsightSummary text={result.summary_text} />
                            {/* Credit warning note */}
                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                                p: 1.25, borderRadius: 2,
                                bgcolor: 'rgba(245,158,11,0.08)',
                                border: '1px dashed rgba(245,158,11,0.3)',
                            }}>
                                <Typography sx={{ fontSize: 14, flexShrink: 0, mt: '1px' }}>⚡</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.65 }}>
                                    <strong style={{ color: '#f59e0b' }}>Note:</strong> AI credits were consumed to generate this response.
                                    This platform is designed for <strong>data analytics queries</strong> only — such as asking about your metrics,
                                    trends, or data breakdowns. Off-domain questions will still be answered briefly, but they are not the
                                    intended use of this assistant.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ── ANSWER card (EXPLAIN / semantic intent) ── */}
                {isAnswer && !isOffDomain && hasNarrative && (
                    <Box sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: 'rgba(var(--primary-rgb), 0.05)',
                        border: '1px solid rgba(var(--primary-rgb), 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', letterSpacing: 0.5, fontSize: 11 }}>
                                Agentic Insight                          </Typography>
                        </Box>
                        <InsightSummary text={result.summary_text} />
                    </Box>
                )}

                {/* ── EMPTY STATE (only for SQL queries with no results, not for EXPLAIN answers) ── */}
                {!hasData && !isAnswer && (
                    <Box sx={{
                        p: 4, my: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', textAlign: 'center', bgcolor: 'rgba(99, 102, 241, 0.03)',
                        borderRadius: 3, border: '1px dashed rgba(99, 102, 241, 0.2)', flex: 1, minHeight: 250
                    }}>
                        <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mb: 1.5, fontSize: 14 }}>
                            No Data Found
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 320, lineHeight: 1.6, fontSize: 12 }}>
                            {result.summary_text || "We couldn't find any matching records for this query. The date range may be too narrow or the category might not exist."}
                        </Typography>
                    </Box>
                )}

                {/* ── DATA Section ── */}
                {hasData && (
                    <SectionShell
                        icon={<StorageIcon sx={{ fontSize: 15 }} />}
                        label="DATA"
                        subtitle="Charts & Visualizations"
                        color="#6366f1"
                        bg="rgba(99, 102, 241, 0.08)"
                        border="rgba(99, 102, 241, 0.2)"
                        pinned={pinnedSections[`data_${result.id}`]}
                        onPin={() => onPin?.(`data_${result.id}`, {
                            id: `data_${result.id || Date.now()}`,
                            title: stripQuery(result.user_prompt),
                            narrative: '', // Omit narrative to avoid duplication with INSIGHTS pin
                            viz_config_json: result.viz_config_json || null,
                            result_json: resultData,
                            intent: result.intent || 'answer',
                        })}
                        defaultOpen={true}
                    >
                        {/* Chart / Table toggle */}
                        {hasData && hasChart && (
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                                <Chip
                                    label="Chart"
                                    size="small"
                                    icon={<BarChartIcon sx={{ fontSize: '12px !important' }} />}
                                    onClick={() => setShowTable(false)}
                                    sx={{
                                        fontSize: 10, height: 22, cursor: 'pointer',
                                        bgcolor: !showTable ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                                        color: !showTable ? '#6366f1' : 'text.disabled',
                                        border: `1px solid ${!showTable ? 'rgba(99, 102, 241, 0.5)' : 'transparent'}`,
                                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.15)' },
                                    }}
                                />
                                <Chip
                                    label="Table"
                                    size="small"
                                    icon={<TableChartIcon sx={{ fontSize: '12px !important' }} />}
                                    onClick={() => setShowTable(true)}
                                    sx={{
                                        fontSize: 10, height: 22, cursor: 'pointer',
                                        bgcolor: showTable ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                                        color: showTable ? '#6366f1' : 'text.disabled',
                                        border: `1px solid ${showTable ? 'rgba(99, 102, 241, 0.5)' : 'transparent'}`,
                                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.15)' },
                                    }}
                                />
                            </Box>
                        )}

                        {/* Key metrics chips */}
                        {metrics.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                                {metrics.map((m, i) => (
                                    <Box key={i} sx={{
                                        px: 1, py: 0.25, borderRadius: 10,
                                        bgcolor: `${m.color}12`, border: `1px solid ${m.color}30`,
                                        display: 'flex', alignItems: 'center', gap: 0.5,
                                    }}>
                                        <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1 }}>
                                            {m.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: m.color, lineHeight: 1 }}>
                                            {m.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Chart or Table view */}
                        {hasChart && !showTable ? (
                            <Box>
                                {result.viz_config_json?.title && (
                                    <Typography variant="caption" sx={{ color: '#6366f1', display: 'block', mb: 0.5, fontWeight: 600, fontSize: 10 }}>
                                        {result.viz_config_json.title}
                                    </Typography>
                                )}
                                <VisualErrorBoundary onRetry={() => window.location.reload()}>
                                    <AiChartRenderer vizConfig={result.viz_config_json} data={resultData} />
                                </VisualErrorBoundary>
                                {hasData && (
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, display: 'block', mt: 0.5 }}>
                                        {resultData.length} row{resultData.length !== 1 ? 's' : ''} of data
                                    </Typography>
                                )}
                            </Box>
                        ) : hasData ? (
                            <VisualErrorBoundary onRetry={() => window.location.reload()}>
                                <MiniTable data={resultData} />
                            </VisualErrorBoundary>
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No chart or table data for this query.
                            </Typography>
                        )}
                    </SectionShell>
                )}

                {/* ── INSIGHTS Section ── */}
                {hasData && (
                    <SectionShell
                        icon={<InsightsIcon sx={{ fontSize: 15 }} />}
                        label="INSIGHTS"
                        subtitle={editingInsight ? 'Editing…' : 'AI Narrative Summary'}
                        color="#6366f1"
                        bg="rgba(99, 102, 241, 0.08)"
                        border={editingInsight ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'}
                        pinned={pinnedSections[`insights_${result.id}`]}
                        onPin={() => {
                            const narrative = savedInsight || result.summary_text || '';
                            onPin?.(`insights_${result.id}`, {
                                id: `insights_${result.id || Date.now()}`,
                                title: stripQuery(result.user_prompt),
                                narrative,
                                viz_config_json: null,
                                result_json: [],
                                intent: 'summarize',
                            });
                        }}
                        onEdit={readOnly ? undefined : () => {
                            setInsightText(savedInsight);
                            setEditingInsight(true);
                        }}
                        defaultOpen={true}
                    >
                        {editingInsight ? (
                            <Box>
                                <TextField
                                    multiline
                                    fullWidth
                                    minRows={5}
                                    maxRows={12}
                                    value={insightText}
                                    onChange={e => setInsightText(e.target.value)}
                                    autoFocus
                                    onClick={e => e.stopPropagation()}
                                    placeholder="Edit the AI narrative…"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: 12.5, lineHeight: 1.75,
                                            borderRadius: 2,
                                            bgcolor: 'background.default',
                                            '& fieldset': { borderColor: 'rgba(167,139,250,0.4)' },
                                            '&:hover fieldset': { borderColor: 'rgba(167,139,250,0.7)' },
                                            '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                    <Button
                                        size="small"
                                        startIcon={<CloseOutlinedIcon sx={{ fontSize: 12 }} />}
                                        onClick={() => setEditingInsight(false)}
                                        sx={{ textTransform: 'none', fontSize: 11, color: 'text.secondary' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<CheckIcon sx={{ fontSize: 12 }} />}
                                        onClick={() => {
                                            setSavedInsight(insightText);
                                            setEditingInsight(false);
                                        }}
                                        sx={{
                                            textTransform: 'none', fontSize: 11,
                                            background: '#6366f1',
                                            opacity: 0.9,
                                            boxShadow: 'none',
                                            '&:hover': { boxShadow: '0 2px 8px rgba(99, 102, 241, 0.4)' },
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        ) : (savedInsight || hasNarrative) ? (
                            <InsightSummary text={savedInsight || result.summary_text} />
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No narrative summary for this query.
                            </Typography>
                        )}
                    </SectionShell>
                )}


                {/* error */}
                {result.status === 'error' && (
                    <Box sx={{ p: 1.5, bgcolor: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: '#f43f5e' }}>
                            ⚠ {result.error_message || 'An error occurred.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Follow-up suggestions — one-click to run */}
            {result.followup_questions && result.followup_questions.length > 0 && onRerun && (
                <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                        ✨ Similar follow-up questions
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {result.followup_questions.map((q, i) => (
                            <Chip
                                key={i}
                                label={q}
                                size="small"
                                onClick={() => onRerun(q)}
                                sx={{
                                    fontSize: 11, cursor: 'pointer', height: 'auto',
                                    '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4, px: 0.75 },
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.07)',
                                    color: '#6366f1',
                                    border: '1px solid rgba(99, 102, 241, 0.25)',
                                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)', borderColor: '#6366f1' },
                                    transition: 'all 0.15s',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

        </Box>
    );
};

export default ResultPanel;
